import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, AlertCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { openCalendly } from "@/lib/calendly";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_URL = "https://calendly.com/leprincemaxim/30min";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const inputCls = "bg-white border-[#E7E5E4] text-[#0A0A0A] placeholder:text-[#A8A29E] h-12 rounded-lg focus-visible:ring-2 focus-visible:ring-[#7C3AED]/25 focus-visible:border-[#7C3AED]";

export default function FinalCta() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      // GTM: fire only after the API confirms success (once per successful submission)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "contact_form_success",
        form_name: "main_contact_form",
      });
      setStatus("success");
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 pb-32 md:pb-28 bg-[#FAFAF7] scroll-mt-20 relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-[#7C3AED]/[0.05] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">Next step</p>
          <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.75rem] font-bold text-[#0A0A0A] leading-[1.15]" data-testid="final-cta-headline">
            Ready to scale your sweepstakes acquisition?
          </h2>
          <p className="text-[15px] md:text-lg text-[#525252] mt-5 md:mt-6 max-w-lg leading-relaxed">
            Tell us where you are today — traffic, tracking, creatives or retention — and we'll review what needs to be fixed before scaling.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly} className="inline-block mt-7 md:mt-9 w-full sm:w-auto">
            <Button data-testid="final-cta-button" className="btn-primary w-full sm:w-auto px-7 py-6 text-base font-semibold rounded-full h-auto">
              Book a Strategy Call <ArrowRight size={18} className="ml-2" />
            </Button>
          </a>
          <ul className="mt-8 md:mt-10 space-y-3">
            {[
              "Sweepstakes-only focus — Meta, TikTok, UGC, tracking",
              "Free acquisition audit on the call",
              "Limited partner roster, fast execution",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-[#525252]">
                <span className="w-5 h-5 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center text-[#7C3AED] shrink-0 mt-0.5">
                  <Check size={11} strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="bg-white border border-[#E7E5E4] rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(10,10,10,0.12)]" data-testid="contact-form-card">
            <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">Prefer to write first?</h3>
            <p className="text-sm text-[#525252] mb-6">Share a few details and we'll come back within one business day.</p>
            {status === "success" && (
              <div className="flex items-center gap-3 border border-[#7C3AED]/25 bg-[#7C3AED]/5 text-[#5B21B6] rounded-lg p-4 mb-5 text-sm" data-testid="contact-success">
                <CheckCircle size={18} /> Message received. We'll get back to you within one business day.
              </div>
            )}
            {status === "error" && (
              <div className="flex items-center gap-3 border border-red-200 bg-red-50 text-red-700 rounded-lg p-4 mb-5 text-sm" data-testid="contact-error">
                <AlertCircle size={18} /> Something went wrong. Please try again or book a call directly.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
              <Input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required data-testid="contact-name" className={inputCls} />
              <Input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required data-testid="contact-email" className={inputCls} />
              <Input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} required data-testid="contact-phone" className={inputCls} />
              <Input type="text" name="company" placeholder="Company / Brand" value={formData.company} onChange={handleChange} required data-testid="contact-company" className={inputCls} />
              <Textarea name="message" placeholder="Tell us about your acquisition goals (optional)" value={formData.message} onChange={handleChange} rows={4} data-testid="contact-message" className={`${inputCls} h-auto py-3 resize-none`} />
              <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full btn-primary py-6 h-auto text-base font-semibold rounded-full">
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function HomeFooter() {
  return (
    <footer className="py-10 border-t border-[#E7E5E4] bg-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center md:justify-between gap-4 text-center md:text-left">
        <span className="flex items-center">
          <img src="/voveron-full-logo.png" alt="Voveron — US sweepstakes marketing agency" className="h-8 w-auto" width="180" height="48" />
        </span>
        <p className="text-sm text-[#737373]">Sweepstakes marketing agency for US operators — paid media, creative, tracking &amp; player value.</p>
        <div className="flex items-center gap-6">
          <Link to="/blog" className="text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors" data-testid="footer-blog-link">Blog</Link>
          <p className="text-sm text-[#737373]">© {new Date().getFullYear()} Voveron. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
